/**
 * Helpers for the Events feature (QR code / invitation sign-up, organizer CRM).
 * Pure functions, except the small "pending event" store at the bottom.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface ParsedEmails {
  valid: string[]
  invalid: string[]
}

/**
 * Splits what an organizer pastes (commas, semicolons, spaces, new lines, an
 * Excel column, "Name <mail>" entries…) into unique lowercase addresses.
 */
export function parseEmailList(raw: string): ParsedEmails {
  const valid: string[] = []
  const invalid: string[] = []
  const seen = new Set<string>()
  for (const token of raw.split(/[\s,;]+/)) {
    const cleaned = token
      .replace(/^[<("']+|[>)"']+$/g, '')
      .trim()
      .toLowerCase()
    if (!cleaned) continue
    if (seen.has(cleaned)) continue
    seen.add(cleaned)
    if (EMAIL_PATTERN.test(cleaned)) valid.push(cleaned)
    else invalid.push(token.trim())
  }
  return { valid, invalid }
}

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim())
}

const LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' }

/**
 * "jeudi 2 octobre · 19:00 – 22:00", or with both dates when the event spans
 * several days ("2 oct. 19:00 – 3 oct. 02:00").
 */
export function formatEventDateRange(startsAt: string, endsAt: string, language = 'fr'): string {
  const locale = LOCALES[language] ?? 'fr-FR'
  const start = new Date(startsAt)
  const end = new Date(endsAt)
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' })
  const sameDay = start.toDateString() === end.toDateString()
  if (sameDay) {
    const day = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(start)
    return `${capitalize(day)} · ${time.format(start)} – ${time.format(end)}`
  }
  const short = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' })
  return `${short.format(start)} ${time.format(start)} – ${short.format(end)} ${time.format(end)}`
}

/** Short date for lists: "2 oct." / "Oct 2". */
export function formatShortDate(value: string, language = 'fr'): string {
  return new Intl.DateTimeFormat(LOCALES[language] ?? 'fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** ISO string → value for `<input type="datetime-local">` in local time. */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** `<input type="datetime-local">` value (local time) → ISO string, or null. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** "YYYY-MM-DDTHH:mm" (local time) → Date, parsed by hand so no engine reads it as UTC. */
export function parseDateTimeLocalValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [year, month, day, hours, minutes] = match.slice(1).map(Number)
  const date = new Date(year, month - 1, day, hours, minutes)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Where a date picker opens when the field is empty: the next full hour. */
export function nextFullHour(now: Date = new Date()): Date {
  const date = new Date(now)
  date.setHours(date.getHours() + 1, 0, 0, 0)
  return date
}

/** "sam. 27 sept. 2026, 19:00" — the value shown in a native date + time field. */
export function formatEventDateTime(date: Date, language = 'fr'): string {
  return new Intl.DateTimeFormat(LOCALES[language] ?? 'fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/** "2 oct. 2026" — a day without the time, for "until …" notices. */
export function formatEventDay(iso: string, language = 'fr'): string {
  return new Intl.DateTimeFormat(LOCALES[language] ?? 'fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso))
}

/** Lower-case, accent-free text for instant search ("Élodie" matches "elodie"). */
export function foldForSearch(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

export function initialsOf(firstName?: string, lastName?: string): string {
  return (
    `${firstName?.trim().charAt(0) ?? ''}${lastName?.trim().charAt(0) ?? ''}`.toUpperCase() || '?'
  )
}

/** Public URL of an event, as encoded in its QR code. */
export function eventPublicUrl(code: string, origin?: string): string {
  const base =
    origin ??
    (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://app.keeptouch.co')
  return `${base.replace(/\/$/, '')}/e/${code}`
}

/** Scheme of the native app links (`keeptouch://e/<CODE>`). */
export const EVENT_APP_SCHEME = 'keeptouch://'

const EVENT_LINK_PATTERN = /^(?:keeptouch:\/\/|https?:\/\/[^/?#\s]+\/)e\/([A-Za-z0-9]{4,32})\/?(?:[?#]|$)/

/**
 * Event code carried by a scanned QR code or an opened link: the web page
 * (`https://…/e/<CODE>`) or the app link (`keeptouch://e/<CODE>`). Null otherwise.
 */
export function eventCodeFromUrl(value: string): string | null {
  const match = EVENT_LINK_PATTERN.exec(value.trim())
  return match ? match[1].toUpperCase() : null
}

/* ---------- LinkedIn ---------- */

export const LINKEDIN_CALLBACK_PATH = '/linkedin/callback'

export function linkedinRedirectUri(origin: string): string {
  return `${origin.replace(/\/$/, '')}${LINKEDIN_CALLBACK_PATH}`
}

/** LinkedIn "Sign In with LinkedIn using OpenID Connect" authorization URL. */
export function linkedinAuthorizeUrl(params: {
  clientId: string
  redirectUri: string
  state: string
}): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    state: params.state,
    scope: 'openid profile email',
  })
  return `https://www.linkedin.com/oauth/v2/authorization?${query.toString()}`
}

/** Local fake mode: the API accepts `fake:` + base64url(JSON identity). */
export function fakeLinkedInCode(identity: {
  email: string
  firstName: string
  lastName?: string
}): string {
  const json = JSON.stringify(identity)
  const base64 = btoa(unescape(encodeURIComponent(json)))
  return `fake:${base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`
}
